import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { marketingCheckStatusTone } from '@/tiktok-platform/utils/tiktokMarketingDisplay';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-drawer.css';

function RequirementList({ title, items }) {
  const { t } = useTranslation('workbench');
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <section className="tko-marketing-drawer__section">
        <h3>{title}</h3>
        <p className="tko-marketing-drawer__muted">{fixedUi("暂无数据")}</p>
      </section>
    );
  }
  return (
    <section className="tko-marketing-drawer__section">
      <h3>{title}</h3>
      <ul className="tko-marketing-drawer__req-list">
        {items.map((item, idx) => {
          const statusText = item?.checkStatusDesc
            ? fixedUi(item.checkStatusDesc)
            : (item?.checkStatus == null ? '' : fixedUiTpl('状态 {0}', item.checkStatus));
          const tone = marketingCheckStatusTone(item?.checkStatus, item?.checkStatusDesc);
          return (
            <li key={item?.requirementCode ?? idx}>
              <strong>{item?.requirementDesc || item?.requirementType || t('marketing.enReqN', { n: idx + 1 })}</strong>
              {statusText ? (
                <span className={`tko-marketing-drawer__req-status tko-marketing-drawer__req-status--${tone}`}>
                  {statusText}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * 活动详情抽屉：详情 / 已报名商品
 */
export default function TiktokMarketingActivityDrawer({
  open,
  shopId,
  activity,
  initialTab = 'detail',
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [subTab, setSubTab] = useState('detail');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detail, setDetail] = useState(null);

  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [products, setProducts] = useState([]);
  const [scrollContext, setScrollContext] = useState(null);
  const [stockDrafts, setStockDrafts] = useState({});
  const [rowBusyId, setRowBusyId] = useState('');

  const thematicId = activity?.activityThematicId || activity?.activityId;
  const activityType = activity?.activityType != null ? String(activity.activityType).trim() : '';

  const loadDetail = useCallback(async () => {
    if (!shopId || !thematicId || !activityType) return;
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await tiktokPlatformAPI.getMarketingActivityDetail(shopId, thematicId, { activityType });
      setDetail(res?.result || res?.activity || res?.thematicInfo || res || null);
      if (res?.success === false && res?.errorMsg) {
        setDetailError(res.errorMsg);
      }
    } catch (e) {
      setDetail(null);
      setDetailError(e?.message || fixedUi('加载活动详情失败'));
    } finally {
      setDetailLoading(false);
    }
  }, [shopId, thematicId, activityType]);

  const loadProducts = useCallback(async ({ reset = false, context } = {}) => {
    if (!shopId || !thematicId || !activityType) return;
    setProductsLoading(true);
    setProductsError('');
    try {
      const nextContext = reset ? undefined : (context !== undefined ? context : (scrollContext || undefined));
      const res = await tiktokPlatformAPI.getMarketingActivityProducts(shopId, thematicId, {
        activityType,
        rowCount: 20,
        searchScrollContext: nextContext,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setProducts((prev) => (reset ? items : [...prev, ...items]));
      setScrollContext(res?.searchScrollContext || null);
      if (res?.success === false && res?.errorMsg) {
        setProductsError(res.errorMsg);
      }
    } catch (e) {
      if (reset) setProducts([]);
      setProductsError(e?.message || fixedUi('加载活动商品失败'));
    } finally {
      setProductsLoading(false);
    }
  }, [shopId, thematicId, activityType, scrollContext]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !activity) return;
    setSubTab(initialTab === 'products' ? 'products' : 'detail');
    setDetail(null);
    setProducts([]);
    setScrollContext(null);
    setStockDrafts({});
    setDetailError('');
    setProductsError('');
    loadDetail();
  }, [open, activity, loadDetail, initialTab]);

  useEffect(() => {
    if (!open || subTab !== 'products') return;
    setProducts([]);
    setScrollContext(null);
    loadProducts({ reset: true, context: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subTab, thematicId, activityType]);

  const saveActivityStock = async (row) => {
    const productId = String(row.productId || row.id || '').trim();
    if (!shopId || !thematicId || !productId) return;
    const variation = String(activity?.productLevel || activity?.product_level || '').toUpperCase() === 'VARIATION';
    const raw = stockDrafts[productId] != null ? stockDrafts[productId] : row.quantityLimit;
    const limit = raw === '' || raw == null ? -1 : Number(raw);
    if (!Number.isFinite(limit) || (limit !== -1 && (limit < 1 || limit > 99))) {
      showToast(t('marketing.enStockRange'), 'warning');
      return;
    }
    const item = {
      productId,
      quantityLimit: variation ? -1 : limit,
      quantityPerUser: variation ? -1 : (row.quantityPerUser != null ? Number(row.quantityPerUser) : -1),
    };
    if (!variation) {
      if (row.discount) item.discount = String(row.discount);
      if (row.activityPriceAmount) item.activityPriceAmount = String(row.activityPriceAmount);
    }
    const skus = Array.isArray(row.skus) ? row.skus : [];
    if (variation && skus.length) {
      item.skus = skus.map((sku) => ({
        id: sku.id,
        quantityLimit: limit,
        quantityPerUser: sku.quantityPerUser != null ? Number(sku.quantityPerUser) : -1,
        ...(sku.discount ? { discount: String(sku.discount) } : {}),
        ...(sku.activityPriceAmount ? { activityPriceAmount: String(sku.activityPriceAmount) } : {}),
      }));
    }
    setRowBusyId(productId);
    try {
      await tiktokPlatformAPI.submitMarketingEnroll(shopId, thematicId, { productList: [item] });
      showToast(t('marketing.enStockUpdated'), 'success');
      loadProducts({ reset: true, context: null });
    } catch (e) {
      showToast(e?.message || t('marketing.enStockFail'), 'error');
    } finally {
      setRowBusyId('');
    }
  };

  const endActivityProduct = async (row) => {
    const productId = String(row.productId || row.id || '').trim();
    if (!shopId || !thematicId || !productId) return;
    setRowBusyId(productId);
    try {
      await tiktokPlatformAPI.removePromotionActivityProducts(shopId, thematicId, {
        product_ids: [productId],
      });
      showToast(t('marketing.enEnded'), 'success');
      loadProducts({ reset: true, context: null });
    } catch (e) {
      showToast(e?.message || t('marketing.enEndFail'), 'error');
    } finally {
      setRowBusyId('');
    }
  };

  if (!open) return null;

  const thematicInfo = detail?.thematicInfo || detail?.activityInfo || {};
  const canEnroll = detail?.activityInfo?.canEnroll ?? detail?.canEnroll;

  return (
    <div className="tiktok-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="tiktok-drawer tiktok-drawer--wide"
        role="dialog"
        aria-labelledby="tko-marketing-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tiktok-drawer__header">
          <div>
            <h2 id="tko-marketing-drawer-title">{activity?.name || activity?.activityName || t('marketing.enDetail')}</h2>
            <p className="tiktok-drawer__subtitle">
              {activity?.activityTypeLabel
                ? fixedUi(activity.activityTypeLabel)
                : (activity?.type ? fixedUi(String(activity.type)) : '—')}
              {thematicId ? ` · ${fixedUiTpl('专题 {0}', thematicId)}` : ''}
            </p>
          </div>
          <button type="button" className="tiktok-drawer__close" onClick={onClose} aria-label={t('moduleShell.close')}>
            ×
          </button>
        </header>

        <div className="tko-marketing-drawer__tabs" role="tablist">
          {[
            { id: 'detail', label: '详情' },
            { id: 'products', label: '活动商品' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className={`tko-status-tab${subTab === tab.id ? ' is-active' : ''}`}
              aria-selected={subTab === tab.id}
              onClick={() => setSubTab(tab.id)}
            >
              {fixedUi(tab.label)}
            </button>
          ))}
        </div>

        <div className="tiktok-drawer__body">
          {subTab === 'detail' ? (
            <>
              {detailLoading ? <p className="tiktok-drawer__hint">{fixedUi("加载详情中…")}</p> : null}
              {!detailLoading && detailError ? <p className="tiktok-drawer__error">{detailError}</p> : null}
              {!detailLoading && !detailError && detail ? (
                <div className="tko-marketing-drawer__detail">
                  <div className="tiktok-detail-grid">
                    <div className="tiktok-detail-row">
                      <span className="tiktok-detail-row__label">{fixedUi("是否可报名")}</span>
                      <span className="tiktok-detail-row__value">
                        {canEnroll == null ? '—' : (canEnroll ? fixedUi("是") : fixedUi("否"))}
                      </span>
                    </div>
                    <div className="tiktok-detail-row">
                      <span className="tiktok-detail-row__label">{fixedUi("活动名称")}</span>
                      <span className="tiktok-detail-row__value">
                        {thematicInfo.activityThematicName
                          || detail?.activityInfo?.activityName
                          || activity?.name
                          || '—'}
                      </span>
                    </div>
                    <div className="tiktok-detail-row">
                      <span className="tiktok-detail-row__label">{fixedUi("报名方式")}</span>
                      <span className="tiktok-detail-row__value">
                        {thematicInfo.enrollSource === 1
                          ? fixedUi("自主报名")
                          : (thematicInfo.enrollSource === 0
                            ? fixedUi('邀约报名')
                            : (activity?.enrollSourceLabel
                              ? fixedUi(activity.enrollSourceLabel)
                              : (Number(activity?.enrollSource) === 1
                                ? fixedUi("自主报名")
                                : (Number(activity?.enrollSource) === 0 ? fixedUi('邀约报名') : '—'))))}
                      </span>
                    </div>
                  </div>
                  <RequirementList title={fixedUi("活动要求")} items={detail?.requirements} />
                  <RequirementList title={fixedUi("店铺资质")} items={detail?.mallAptitude} />
                </div>
              ) : null}
              {!detailLoading && !detailError && !detail ? (
                <p className="tiktok-drawer__hint">{fixedUi("暂无详情")}</p>
              ) : null}
            </>
          ) : null}

          {subTab === 'products' ? (
            <>
              <p className="tko-marketing-drawer__muted">{fixedUi("新商品请从活动列表的「提报商品」进入报名页选择。")}</p>
              {productsError ? <p className="tiktok-drawer__error">{productsError}</p> : null}
              <div className="tko-drafts__table-wrap tko-ops-table-wrap">
                <table className="tko-publish__table tko-drafts__table tko-ops-table">
                  <thead>
                    <tr>
                      <th scope="col">{fixedUi("商品 ID")}</th>
                      <th scope="col">{fixedUi("优惠")}</th>
                      <th scope="col">{fixedUi("已用")}</th>
                      <th scope="col">{fixedUi("活动库存")}</th>
                      <th scope="col">{fixedUi("操作")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 && !productsLoading ? (
                      <tr>
                        <td colSpan={5} className="tko-certs-empty-cell">{fixedUi("活动里还没有商品")}</td>
                      </tr>
                    ) : (
                      products.map((row) => {
                        const id = String(row.productId || row.id || '');
                        const offer = row.activityPriceAmount
                          ? `${row.activityPriceAmount} ${row.currency || ''}`.trim()
                          : (row.discount ? `${row.discount}%` : '—');
                        return (
                          <tr key={id}>
                            <td>{id || '—'}</td>
                            <td>{offer}</td>
                            <td>{row.usedQuantity != null ? row.usedQuantity : '—'}</td>
                            <td>
                              <input
                                className="tko-input"
                                style={{ width: 88 }}
                                value={stockDrafts[id] != null ? stockDrafts[id] : (row.quantityLimit ?? '')}
                                onChange={(e) => setStockDrafts((prev) => ({ ...prev, [id]: e.target.value }))}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="tko-btn tko-btn--secondary tko-btn--sm"
                                disabled={rowBusyId === id}
                                onClick={() => saveActivityStock(row)}
                              >
                                {t('marketing.enSaveStock')}
                              </button>
                              <button
                                type="button"
                                className="tko-btn tko-btn--ghost tko-btn--sm"
                                disabled={rowBusyId === id}
                                onClick={() => endActivityProduct(row)}
                              >
                                {t('marketing.enEndGoods')}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="tko-marketing-drawer__foot">
                {productsLoading ? <span className="tko-marketing-drawer__muted">{fixedUi("加载活动商品中…")}</span> : null}
              </div>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
